import React, { useState, useEffect } from 'react';
import { FiActivity, FiFilter, FiCalendar, FiUser } from 'react-icons/fi';
import activityLogService from '../services/activityLogService';
import './ActivityLogs.css';

const ActivityLogs = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    action: '',
    entity_type: '',
    user_id: '',
    start_date: '',
    end_date: '',
    page: 1,
    limit: 50
  });
  const [pagination, setPagination] = useState({});
  const [stats, setStats] = useState(null);
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'timeline'

  useEffect(() => {
    fetchLogs();
    fetchStats();
  }, [filters]);

  const fetchLogs = async () => {
    try {
      setLoading(true);
      const data = await activityLogService.getLogs(filters);
      setLogs(data.logs.map(log => activityLogService.formatLog(log)));
      setPagination(data.pagination || {});
      setError(null);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch activity logs');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const data = await activityLogService.getStats(filters);
      setStats(data);
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value, page: 1 }));
  };

  const handleClearFilters = () => {
    setFilters({
      action: '',
      entity_type: '',
      user_id: '',
      start_date: '',
      end_date: '',
      page: 1,
      limit: 50
    });
  };

  const groupedLogs = viewMode === 'timeline' 
    ? activityLogService.groupByDate(logs)
    : { 'All Activity': logs };

  if (loading && logs.length === 0) {
    return <div className="loading">Loading activity logs...</div>;
  }

  return (
    <div className="activity-logs-page">
      <div className="page-header">
        <div className="header-left">
          <h1><FiActivity /> Activity Logs</h1>
          {stats && (
            <div className="activity-stats">
              <span className="stat-badge">{stats.total} Total Activities</span>
            </div>
          )}
        </div>
        <div className="header-actions">
          <div className="view-toggle">
            <button
              className={viewMode === 'list' ? 'active' : ''}
              onClick={() => setViewMode('list')}
            >
              List
            </button>
            <button
              className={viewMode === 'timeline' ? 'active' : ''}
              onClick={() => setViewMode('timeline')}
            >
              Timeline
            </button>
          </div>
        </div>
      </div>

      <div className=" flex flex-wrap p-4 border-b border-gray-200" style={{gap:"16px"}}>
        <div className="card p-4 flex items-center gap-2">
          <FiFilter />
          <select name="action" value={filters.action} onChange={handleFilterChange}>
            <option value="">All Actions</option>
            <option value="created">Created</option>
            <option value="updated">Updated</option>
            <option value="deleted">Deleted</option>
            <option value="assigned">Assigned</option>
            <option value="completed">Completed</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
          </select>
        </div>

        <div className="card p-4 flex items-center gap-2">
          <select name="entity_type" value={filters.entity_type} onChange={handleFilterChange}>
            <option value="">All Entity Types</option>
            <option value="Project">Projects</option>
            <option value="Task">Tasks</option>
            <option value="Issue">Issues</option>
            <option value="Document">Documents</option>
            <option value="Budget">Budgets</option>
            <option value="Risk">Risks</option>
            <option value="Milestone">Milestones</option>
          </select>
        </div>

        <div className="card p-4 flex items-center gap-2">
          <FiCalendar />
          <input
            type="date"
            name="start_date"
            value={filters.start_date}
            onChange={handleFilterChange}
            placeholder="Start Date"
          />
        </div>

        <div className="card p-4 flex items-center gap-2">
          <FiCalendar />
          <input
            type="date"
            name="end_date"
            value={filters.end_date}
            onChange={handleFilterChange}
            placeholder="End Date"
          />
        </div>

        {(filters.action || filters.entity_type || filters.start_date || filters.end_date) && (
          <button className="btn btn-secondary" onClick={handleClearFilters}>
            Clear Filters
          </button>
        )}
      </div>

      {stats && (
        <div className="activity-stats-grid">
          <div className="stat-card">
            <h3>By Action</h3>
            <div className="stat-list">
              {Object.entries(stats.byAction || {}).map(([action, count]) => {
                const config = activityLogService.getActionConfig(action);
                return (
                  <div key={action} className="stat-item">
                    <span className="stat-icon" style={{ color: config.color }}>
                      {config.icon}
                    </span>
                    <span className="stat-label">{config.label}</span>
                    <span className="stat-value">{count}</span>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="stat-card">
            <h3>By Entity Type</h3>
            <div className="stat-list">
              {Object.entries(stats.byEntityType || {}).map(([type, count]) => {
                const config = activityLogService.getEntityTypeConfig(type);
                return (
                  <div key={type} className="stat-item">
                    <span className="stat-icon">{config.icon}</span>
                    <span className="stat-label">{config.label}</span>
                    <span className="stat-value">{count}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {error && <div className="error-message">{error}</div>}

      <div className="activity-content">
        {logs.length === 0 ? (
          <div className="empty-state">
            <FiActivity size={64} />
            <h3>No activity logs</h3>
            <p>No activity found for the selected filters</p>
          </div>
        ) : (
          Object.entries(groupedLogs).map(([dateGroup, groupLogs]) => (
            groupLogs.length > 0 && (
              <div key={dateGroup} className="activity-group">
                {viewMode === 'timeline' && <h3 className="group-title">{dateGroup}</h3>}
                <div className={`activity-list ${viewMode}`}>
                  {groupLogs.map(log => (
                    <div key={log.id} className="activity-item">
                      <div className="activity-icon" style={{ backgroundColor: log.actionColor }}>
                        {log.actionIcon}
                      </div>
                      <div className="activity-content-wrapper">
                        <div className="activity-header">
                          <div className="activity-user">
                            <FiUser />
                            <span>{log.userName}</span>
                          </div>
                          <span className="activity-time">{log.timeAgo}</span>
                        </div>
                        <div className="activity-description">
                          <span className="activity-action">{log.actionLabel}</span>
                          <span className="activity-entity">
                            {log.entityIcon} {log.entityLabel}
                          </span>
                        </div>
                        {log.description && (
                          <p className="activity-detail">{log.description}</p>
                        )}
                        {log.changes && Object.keys(log.changes).length > 0 && (
                          <div className="activity-changes">
                            {activityLogService.formatChanges(log.changes).map((change, idx) => (
                              <div key={idx} className="change-item">
                                <span className="change-field">{change.field}:</span>
                                <span className="change-before">{String(change.before)}</span>
                                <span className="change-arrow">→</span>
                                <span className="change-after">{String(change.after)}</span>
                              </div>
                            ))}
                          </div>
                        )}
                        <div className="activity-meta">
                          <span className="activity-date">{log.formattedDate}</span>
                          {log.ip_address && (
                            <span className="activity-ip">IP: {log.ip_address}</span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )
          ))
        )}
      </div>

      {pagination.pages > 1 && (
        <div className="pagination">
          <button
            disabled={filters.page === 1}
            onClick={() => setFilters(prev => ({ ...prev, page: prev.page - 1 }))}
          >
            Previous
          </button>
          <span>Page {filters.page} of {pagination.pages}</span>
          <button
            disabled={filters.page === pagination.pages}
            onClick={() => setFilters(prev => ({ ...prev, page: prev.page + 1 }))}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default ActivityLogs;


